import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { submitCompanyDetails, resendVerificationCode } from '../../api/registration';
import { getEORIValidation } from '../../api/util';
import Parser from 'html-react-parser';

import { RegistrationContext } from './RegistrationContainer';
import { StoreContext } from '../../components/Store';
import SSORedirectModal from '../Decs/Modals/SSORedirectModal';

const isValidContactName = (name) => {
  const nameRegex = /^[a-zA-Z\s]+$/;
  return nameRegex.test(name) && name.trim().length > 3;
};

const CompanyDetails = () => {
  const { formData, setError, handleChange, countries, newErrors, isTransferryCustomer } =
    useContext(RegistrationContext);

  const { isSSOUserWithNoCompany } = useContext(StoreContext);
  const isSSOUser = isSSOUserWithNoCompany[0];

  const [docusignLoading, setDocusignLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [showSSORedirectModal, setShowSSORedirectModal] = useState(false);

  useEffect(() => {
    if (isTransferryCustomer && !isSSOUser) {
      setShowSSORedirectModal(true);
    }
  }, [isTransferryCustomer, isSSOUser]);

  const handleResendCode = async () => {
    try {
      const response = await resendVerificationCode(formData.email);
      console.log('Verification code resent:', response);
      setPopupMessage(`Verification Email is sent to ${formData.email}. Please check your junk folder.`);
      setPopupOpen(true);
      setError('');
    } catch (error) {
      console.error('Error resending verification code:', error);
      setError('Failed to resend verification code. Please try again.');
    }
  };
  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  const handleNext = async () => {
    const fieldLabels = {
      verificationCode: 'Verification Code',
      name: 'Company Name',
      eoriNumber: 'Eori Number',
      address: 'Address',
      town: 'Town/City',
      postcode: 'Postcode',
      country: 'Country',
      contactName: 'Contact Name',
      contactEmail: 'Contact Email',
      contactPhone: 'Contact Phone',
    };

    const requiredFields = Object.keys(fieldLabels);

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${fieldLabels[field]} is required`;
      }
    });

    const eoriValidation = await getEORIValidation(formData.eoriNumber, 'true');

    if (eoriValidation.statusDescr === 'In Valid') {
      newErrors['eoriNumber'] = 'Please enter a valid GB EORI Number.';
    }

    // Reset errors
    if (formData.name.length < 4) {
      newErrors['name'] = 'Please enter a valid Company Name.';
    }
    if (!isValidContactName(formData.contactName)) {
      newErrors['contactName'] = 'Please enter a valid name.';
    }

    setFormErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return; // Stop if there are validation errors
    }

    try {
      setDocusignLoading(true);
      const response = await submitCompanyDetails(formData);
      setError('');
      if (!response.consent_required && response.redirect_uri) {
        window.location.href = response.redirect_uri;
      }
    } catch (err) {
      if (Object.keys(err.errors).length) {
        setDocusignLoading(false);
        setFormErrors((prevErrors) => {
          const updatedErrors = {
            ...prevErrors,
            ...err.errors,
          };
          return updatedErrors;
        });
      } else {
        setError('Failed to submit company details. Please try again.');
      }
    }
  };
  return (
    <Box>
      <SSORedirectModal isOpen={showSSORedirectModal} />

      {docusignLoading && (
        <Backdrop sx={() => ({ color: '#fff', zIndex: 999999999 })} open={docusignLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Dialog
        open={popupOpen}
        onClose={handleClosePopup}
        aria-labelledby="verification-popup-title"
        aria-describedby="verification-popup-description"
      >
        <DialogTitle id="verification-popup-title">Verification Email Sent</DialogTitle>
        <DialogContent>
          <Typography id="verification-popup-description">{popupMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Typography variant="body2" gutterBottom>
        Please enter your verification code, it may be in your junk folder. If you have not received a verification
        code, please click{' '}
        <Button variant="text" onClick={handleResendCode}>
          Resend
        </Button>
        .
      </Typography>
      <TextField
        fullWidth
        label="Verification Code"
        variant="outlined"
        name="verificationCode"
        value={formData.verificationCode}
        onChange={handleChange}
        sx={{ my: 2 }}
        error={!!formErrors.verificationCode}
        helperText={formErrors.verificationCode}
        autoComplete="off"
        inputProps={{ autoComplete: 'new-code', form: 'off' }}
      />
      <Typography variant="h6" gutterBottom>
        Company Details
      </Typography>
      <TextField
        fullWidth
        label="Company Name"
        variant="outlined"
        name="name"
        value={formData.name}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.name} // Error state for the field
        helperText={formErrors.name} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-company-name', form: 'off' }}
      />

      <TextField
        fullWidth
        label="EORI Number"
        variant="outlined"
        name="eoriNumber"
        value={formData.eoriNumber}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.eoriNumber} // Error state for the field
        helperText={formErrors.eoriNumber && Parser(formErrors.eoriNumber)} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-eori', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Address"
        variant="outlined"
        name="address"
        value={formData.address}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.address} // Error state for the field
        helperText={formErrors.address} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-address', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Town/City"
        variant="outlined"
        name="town"
        value={formData.town}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.town} // Error state for the field
        helperText={formErrors.town} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-town', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Postcode"
        variant="outlined"
        name="postcode"
        value={formData.postcode}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.postcode} // Error state for the field
        helperText={formErrors.postcode} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-postcode', form: 'off' }}
      />

      <TextField
        select // Makes this a dropdown
        fullWidth
        label="Country"
        variant="outlined"
        name="country"
        value={formData.country}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.country} // Error state for the field
        helperText={formErrors.country} // Error message
      >
        {countries.map((country) => (
          <MenuItem key={country.id} value={country.id}>
            {country.countryName}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        label="Contact Name"
        variant="outlined"
        name="contactName"
        value={formData.contactName}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.contactName} // Error state for the field
        helperText={formErrors.contactName} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-name', form: 'off' }}
      />

      <TextField
        fullWidth
        label="Contact Email"
        variant="outlined"
        name="contactEmail"
        value={formData.contactEmail}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.contactEmail} // Error state for the field
        helperText={formErrors.contactEmail} // Error message
        disabled
      />

      <TextField
        fullWidth
        label="Contact Phone"
        variant="outlined"
        name="contactPhone"
        value={formData.contactPhone}
        onChange={handleChange}
        sx={{ my: 1 }}
        error={!!formErrors.contactPhone} // Error state for the field
        helperText={formErrors.contactPhone} // Error message
        autoComplete="off"
        inputProps={{ autoComplete: 'new-phone', form: 'off' }}
      />

      <Typography variant="body2" gutterBottom>
        The next step will take you to our partner site (DocuSign) in order to sign the terms and conditions.
      </Typography>
      <Button variant="contained" onClick={handleNext} sx={{ mt: 2 }}>
        Continue
      </Button>
    </Box>
  );
};

export default CompanyDetails;
